/**
 * Copy a project document to tenant-shared knowledge (decision D1).
 *
 * A copy, never a move: the original stays project-private. The copy is
 * re-scanned and re-classified as a tenant document, outlives a purge of this
 * project, and is visible to every project in the tenant — which is why the
 * promoter writes a justification and accepts an attestation, both recorded.
 */
import { useEffect, useState } from "react";
import { Loader2, Share2, X } from "lucide-react";
import { knowledgeHub, type KnowledgeItem } from "../../api/knowledgeHub";

interface Props {
  item: KnowledgeItem;
  onClose: () => void;
  onPromoted: (status: string) => void;
}

export default function PromoteDocumentDialog({
  item,
  onClose,
  onPromoted,
}: Props) {
  const [attestationText, setAttestationText] = useState("");
  const [minLength, setMinLength] = useState(30);
  const [justification, setJustification] = useState("");
  // The copy is shown to every project; the project document's filename is not shared, so the copy is named here.
  const [title, setTitle] = useState("");
  const [attested, setAttested] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    knowledgeHub
      .attestation()
      .then((a) => {
        setAttestationText(a.attestation);
        setMinLength(a.min_justification);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Failed to load"),
      );
  }, []);

  const ready =
    justification.trim().length >= minLength &&
    attested &&
    !!attestationText &&
    title.trim().length > 0;

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const result = await knowledgeHub.promote({
        source_ref: item.ref,
        justification: justification.trim(),
        attested,
        title: title.trim(),
      });
      onPromoted(result.document.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to share");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="promote-title"
      data-testid="promote-dialog"
    >
      <div className="w-full max-w-lg rounded-2xl bg-surface-200 border border-white/10 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Share2 size={16} className="text-brand-400" />
          <h3
            id="promote-title"
            className="text-sm font-bold text-white flex-1"
          >
            Share with every project in this tenant
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-white/40 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>
        <p className="text-xs text-white/50">
          A copy of <span className="text-white/80">{item.title}</span> becomes
          tenant-shared knowledge. This project keeps its own document. The copy
          is scanned and classified again, and stays after this project is
          purged.
        </p>
        <label className="block text-xs text-white/50">
          Title of the shared copy (the project filename is not shared)
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={300}
            data-testid="promote-title"
            className="mt-1 w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white"
          />
        </label>
        <label className="block text-xs text-white/50">
          Justification (at least {minLength} characters)
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={3}
            maxLength={2000}
            data-testid="promote-justification"
            className="mt-1 w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white"
          />
        </label>
        <label className="flex items-start gap-2 text-xs text-white/70">
          <input
            type="checkbox"
            checked={attested}
            onChange={(e) => setAttested(e.target.checked)}
            data-testid="promote-attest"
            className="mt-0.5 accent-brand-500"
          />
          <span>{attestationText || "Loading…"}</span>
        </label>
        {error && (
          <div className="text-xs text-red-400" data-testid="promote-error">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm text-white/60 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!ready || saving}
            data-testid="promote-submit"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40"
            style={{ background: "linear-gradient(135deg, #86BC25, #6B9B1E)" }}
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Share copy
          </button>
        </div>
      </div>
    </div>
  );
}
